import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import { useMe, usePrivateState, useSnapshot } from './store/gameStore';
import Board from './components/Board';
import Dice from './components/Dice';
import Chat from './components/Chat';
import GameFeed from './components/GameFeed';
import { playCapture } from './sound';
import { COLOR_LABELS } from '../../shared/board';
import type { Color } from '../../shared/board';

const MARBLES: Record<Color, string> = {
  red: '#ff5a5a',
  green: '#35d07f',
  yellow: '#ffce3d',
  blue: '#4aa8ff',
};

export default function Game({ socket }: { socket: GameSocketApi }) {
  const snapshot = useSnapshot();
  const me = useMe();
  const privateState = usePrivateState();

  const captured = snapshot?.lastCapture
    ? snapshot.players.find((p) => p.id === snapshot.lastCapture!.victimId) ?? null
    : null;
  const lastCaptureIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = captured?.id ?? null;
    if (id && id !== lastCaptureIdRef.current) {
      playCapture();
    }
    lastCaptureIdRef.current = id;
  }, [captured]);

  if (!snapshot || !me) return null;

  const myColor = me.color;
  const myTurn = snapshot.currentColor === myColor && snapshot.phase === 'playing';
  const canRoll = !!privateState?.youCanRoll;
  const movableTokens = myTurn ? (privateState?.movableTokens ?? []) : [];
  const waitingForMove = snapshot.waitingForMove && myTurn;
  const currentPlayer = snapshot.players.find((p) => p.color === snapshot.currentColor);

  const target = targetText({ snapshot, myTurn, canRoll, waitingForMove, movableTokens, currentPlayer });
  const winner = snapshot.winnerId ? snapshot.players.find((p) => p.id === snapshot.winnerId) : null;
  const capturedName = captured?.name ?? null;
  const currentIsBot = !!currentPlayer?.isBot;

  const onRoll = () => {
    if (canRoll) socket.rollDice();
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#1a1240] via-lud-bg to-[#170f33]">
      <ArenaBackdrop currentColor={myTurn ? myColor : null} />

      <div className="relative z-10 mx-auto w-full max-w-[1560px] px-4 py-6 pb-24 sm:px-6">
        {/* Top status bar */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <span className="lud-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-lud-ink">
            <span className="text-[10px] font-bold uppercase tracking-widest text-lud-muted">Room</span>
            <span className="font-mono font-bold tracking-[0.18em] text-[#ffd98a]">{snapshot.roomCode}</span>
          </span>

          {snapshot.phase === 'gameover' ? (
            <span className="lud-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-[#ffd98a]">
              🏆 Game over
            </span>
          ) : myTurn ? (
            <span
              className="lud-gradient inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ffd257] via-[#ff8a3d] to-[#ff5f16] px-4 py-1.5 text-sm font-bold text-white shadow-glow"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
              </span>
              {target}
            </span>
          ) : (
            <span className="lud-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-lud-ink">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: currentPlayer ? MARBLES[currentPlayer.color] : '#888' }}
              />
              {target}
            </span>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)_330px]">
          {/* Left panel — players & scores */}
          <div className="order-2 lg:order-none">
            <div className="lud-glass rounded-3xl p-4">
              <p className="mb-3 px-1 text-xs font-bold uppercase tracking-widest text-lud-muted">
                Players
              </p>
              <ul className="space-y-2">
                {snapshot.players.map((p) => {
                  const isTurn = snapshot.phase === 'playing' && p.color === snapshot.currentColor;
                  const home = p.tokens.filter((t) => t === 57).length;
                  const onBoard = p.tokens.filter((t) => t > 0 && t < 57).length;
                  const isBot = !!p.isBot;
                  return (
                    <li
                      key={p.id}
                      className={`flex items-center gap-2.5 rounded-2xl px-3 py-2.5 transition-all duration-300 ${
                        isTurn
                          ? 'bg-white/[0.09] ring-1 ring-white/25 shadow-[0_0_24px_-8px_rgba(255,255,255,0.4)]'
                          : 'bg-white/[0.04]'
                      }`}
                    >
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white/30 text-xs font-bold text-white"
                        style={{
                          background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${MARBLES[p.color]} 45%, rgba(0,0,0,0.3) 130%)`,
                        }}
                      >
                        {isBot ? '🤖' : p.name.slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-lud-ink">
                          {p.name} {p.id === me.id && <span className="text-lud-muted/70">(you)</span>}
                          {isBot && (
                            <span className="ml-1.5 rounded-full bg-white/10 px-1.5 py-px text-[10px] font-bold uppercase text-lud-muted">
                              {p.botDifficulty ?? 'bot'}
                            </span>
                          )}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="flex gap-0.5" title={`${home}/4 home`}>
                            {[0, 1, 2, 3].map((i) => (
                              <span
                                key={i}
                                className="h-1.5 w-3 rounded-full"
                                style={{ backgroundColor: i < home ? MARBLES[p.color] : 'rgba(255,255,255,0.15)' }}
                              />
                            ))}
                          </div>
                          <span className="text-[10px] font-bold text-lud-muted">{home}/4 home</span>
                        </div>
                      </div>
                      {onBoard > 0 && (
                        <span className="shrink-0 rounded-full bg-white/[0.07] px-2 py-0.5 text-[10px] font-bold text-lud-muted">
                          {onBoard} out
                        </span>
                      )}
                      {p.rank && (
                        <span className="shrink-0 rounded-full bg-gradient-to-br from-[#ffd257] to-[#ff9a4a] px-2 py-0.5 text-[10px] font-bold text-white shadow">
                          #{p.rank}
                        </span>
                      )}
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${p.isBot || p.connected ? 'bg-emerald-400' : 'bg-white/20'}`}
                        title={p.isBot ? 'bot' : p.connected ? 'connected' : 'disconnected'}
                      />
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 px-1 text-[11px] leading-snug text-lud-muted/80">
                You are <span className="font-bold" style={{ color: MARBLES[myColor] }}>{COLOR_LABELS[myColor]}</span> — roll a 6 to launch a token.
              </p>
            </div>
          </div>

          {/* Board */}
          <div className="relative flex order-1 items-center justify-center lg:order-none">
            <div
              className="pointer-events-none absolute inset-0 opacity-60 transition-opacity duration-700"
              style={{
                background:
                  myTurn && canRoll
                    ? 'radial-gradient(circle at 50% 45%, rgba(255,201,77,0.16) 0%, transparent 62%)'
                    : 'radial-gradient(circle at 50% 45%, rgba(255,255,255,0.05) 0%, transparent 62%)',
                filter: 'blur(6px)',
              }}
              aria-hidden="true"
            />
            <div className="aspect-square w-full max-w-[calc(100dvh-168px)]">
              <Board
                players={snapshot.players}
                myColor={myColor}
                movableTokens={movableTokens}
                currentColor={snapshot.currentColor}
                onMoveToken={(i) => socket.moveToken(i)}
              />
            </div>
          </div>

          {/* Side panel */}
          <aside className="order-3 flex flex-col gap-4 lg:order-none">
            {/* Dice + turn */}
            <div className="lud-glass relative overflow-hidden rounded-3xl p-5">
              <div className="pointer-events-none absolute -right-10 -top-14 h-36 w-36 rounded-full bg-[#ff8a3d]/15 blur-2xl" aria-hidden="true" />
              <div className="relative flex items-center gap-4 pb-9">
                <Dice value={snapshot.dice} canRoll={canRoll} onRoll={onRoll} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-lg font-bold text-lud-ink">
                    {myTurn ? (
                      canRoll ? (
                        <span className="lud-glow-pulse text-transparent bg-clip-text bg-gradient-to-r from-[#ffe58a] to-[#ff9a4a]">
                          Your turn — roll! 🎲
                        </span>
                      ) : (
                        'Your turn'
                      )
                    ) : (
                      <>
                        <span className="mr-1.5 inline-block h-4 w-4 rounded-full align-[-2px]" style={{ backgroundColor: currentPlayer ? MARBLES[currentPlayer.color] : '#888' }} />
                        {currentPlayer?.name ?? '…'}
                        <span className="text-lud-muted">'s turn</span>
                      </>
                    )}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-lud-muted">
                    {kaniText({ myTurn, canRoll, waitingForMove, movableTokens, capturedName: capturedName ?? null, winner: winner ?? null, diceValue: snapshot.dice, passPending: snapshot.passPending, currentIsBot })}
                  </p>
                </div>
              </div>

              {snapshot.phase === 'playing' && snapshot.turnDeadlineAt != null && (
                <TurnTimer snapshot={snapshot} />
              )}

              {snapshot.extraTurn && snapshot.phase === 'playing' && (
                <p className="lud-pop relative mt-3 rounded-xl bg-[#ffce3d]/15 px-3 py-2 text-center text-xs font-bold text-[#ffe58a] ring-1 ring-[#ffce3d]/30">
                  🎲 Six! Roll again — extra turn.
                </p>
              )}
              {snapshot.lastRoll?.forfeited && (
                <p className="lud-pop relative mt-3 rounded-xl bg-red-500/15 px-3 py-2 text-center text-xs font-bold text-red-300 ring-1 ring-red-400/30">
                  💥 Three sixes — turn lost!
                </p>
              )}
            </div>

            {/* Game log */}
            <GameFeed entries={snapshot.chat} />

            {/* Chat */}
            <Chat entries={snapshot.chat} onSend={(t) => socket.sendChat(t)} />
          </aside>
        </div>
      </div>

      {/* Winner overlay */}
      {snapshot.phase === 'gameover' && winner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/70 px-6 backdrop-blur-md">
          <Celebration />
          <div className="relative w-full max-w-sm rounded-[28px] border border-white/15 bg-gradient-to-b from-[#241a4d] to-[#170f33] p-8 text-center shadow-soft">
            <div className="lud-trophy mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#ffd257]/30 to-[#ff5f16]/30 ring-2 ring-[#ffc94d]/50 shadow-glow-gold">
              <span className="text-6xl">🏆</span>
            </div>
            <h2 className="mt-4 bg-gradient-to-br from-[#ffe9a8] via-[#ffc94d] to-[#ff8a3d] bg-clip-text font-display text-4xl font-bold text-transparent">
              {winner.name} wins!
            </h2>
            <p className="mt-2 text-sm text-lud-muted">
              <span className="font-bold" style={{ color: MARBLES[winner.color] }}>
                {COLOR_LABELS[winner.color]}
              </span>{' '}
              got all four tokens home first.
            </p>

            <div className="mt-6 space-y-1.5 text-left">
              {snapshot.players
                .filter((p) => p.rank)
                .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
                .map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-xl bg-white/[0.06] px-3 py-2.5"
                  >
                    <span className="flex items-center gap-2.5 font-semibold text-lud-ink">
                      <span className="text-sm">{[ '🥇', '🥈', '🥉', '4️⃣' ][i] ?? '🎖️'}</span>
                      <span
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: MARBLES[p.color] }}
                      />
                      {p.name}
                    </span>
                    <span className="text-xs font-bold text-lud-muted">#{p.rank}</span>
                  </div>
                ))}
            </div>

            <button
              onClick={() => socket.leave()}
              className="lud-gradient mt-7 w-full rounded-xl bg-gradient-to-r from-[#ffd257] via-[#ff8a3d] to-[#ff5f16] py-3.5 text-sm font-bold tracking-wide text-white shadow-glow transition hover:brightness-110 active:scale-[0.98]"
            >
              Play again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Live countdown for the current turn, rendered as a progress bar. */
function TurnTimer({ snapshot }: { snapshot: NonNullable<ReturnType<typeof useSnapshot>> }) {
  const [now, setNow] = useState(() => Date.now());
  const deadline = snapshot.turnDeadlineAt;
  const playing = snapshot.phase === 'playing';

  useEffect(() => {
    if (!playing || deadline == null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [playing, deadline]);

  if (!playing || deadline == null) return null;

  const total = snapshot.turnTimeoutMs || 1;
  const remaining = Math.max(0, deadline - now);
  const pct = Math.max(0, Math.min(1, remaining / total));
  const secs = Math.ceil(remaining / 1000);
  const low = remaining <= 10_000;

  return (
    <div className="relative mt-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-lud-muted">Turn timer</span>
        <span className={`font-mono text-xs font-bold ${low ? 'text-red-300' : 'text-lud-ink'}`}>{secs}s</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className={`h-full rounded-full transition-[width] duration-200 ease-linear ${
            low
              ? 'bg-gradient-to-r from-red-500 to-orange-400'
              : 'bg-gradient-to-r from-[#ffd257] to-[#ff8a3d]'
          }`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
    </div>
  );
}

/** Ambient glow orbs behind the board, tinted by whose turn it is. */
function ArenaBackdrop({ currentColor }: { currentColor: Color | null }) {
  const base = currentColor ? MARBLES[currentColor] : null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      <div
        className="absolute -top-32 -left-24 h-96 w-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: base ? `${base}22` : 'rgba(255,138,61,0.12)' }}
      />
      <div
        className="absolute top-1/3 -right-28 h-96 w-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: base ? `${base}1c` : 'rgba(74,168,255,0.12)' }}
      />
      <div className="absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-[#35d07f]/10 blur-3xl" />
      <div className="absolute bottom-1/3 -left-20 h-72 w-72 rounded-full bg-[#ffce3d]/10 blur-3xl" />
    </div>
  );
}

/** Falling confetti for the win screen. */
function Celebration() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2.4,
        duration: 3.2 + Math.random() * 2.6,
        size: 7 + Math.random() * 8,
        color: ['#ff5a5a', '#35d07f', '#ffce3d', '#4aa8ff', '#ffc94d', '#ff8a3d'][i % 6],
        round: Math.random() > 0.5,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="lud-confetti"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.55,
            backgroundColor: p.color,
            borderRadius: p.round ? '50%' : '3px',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function targetText({
  snapshot,
  myTurn,
  canRoll,
  waitingForMove,
  movableTokens,
  currentPlayer,
}: {
  snapshot: NonNullable<ReturnType<typeof useSnapshot>>;
  myTurn: boolean;
  canRoll: boolean;
  waitingForMove: boolean;
  movableTokens: number[];
  currentPlayer: { id: string; name: string; isBot?: boolean } | undefined;
}) {
  if (snapshot.phase === 'gameover') return 'Game over';
  if (snapshot.passPending) return 'No moves — passing…';
  if (myTurn) {
    if (waitingForMove && movableTokens.length > 0) return 'Pick a token to move';
    if (waitingForMove) return 'No moves — passing…';
    if (canRoll) return 'It’s your turn — roll!';
  }
  if (currentPlayer?.isBot) return `🤖 ${currentPlayer?.name} is thinking…`;
  if (snapshot.extraTurn) return `Extra turn for ${currentPlayer?.name}`;
  return `${currentPlayer?.name} is moving…`;
}

function kaniText({
  myTurn,
  canRoll,
  waitingForMove,
  movableTokens,
  capturedName,
  winner,
  diceValue,
  passPending,
  currentIsBot,
}: {
  myTurn: boolean;
  canRoll: boolean;
  waitingForMove: boolean;
  movableTokens: number[];
  capturedName: string | null;
  winner: { name: string } | null;
  diceValue: number;
  passPending: boolean;
  currentIsBot?: boolean;
}) {
  if (winner) return `Game over — ${winner.name} won!`;
  if (capturedName) return `Captured ${capturedName}'s token — it's back to the yard!`;
  if (myTurn && passPending) return 'No legal moves — passing…';
  if (myTurn && waitingForMove && movableTokens.length > 0) {
    return `Rolled a ${diceValue}. Tap a glowing token.`;
  }
  if (myTurn && waitingForMove) return 'No legal moves — passing…';
  if (myTurn && canRoll) return 'Tap the die to roll.';
  if (currentIsBot) return 'Bot is thinking…';
  return 'Waiting for the other players…';
}